import { useEffect, useState } from "react";
import { api, API, fmtMoney, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus, Trash2, Eye, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  ArrowLeft, Check, X, Loader2, Pencil, FileSpreadsheet, PenLine,
} from "lucide-react";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function exportXlsx(pid) {
  const url = `${API}/presupuesto/${pid}/export?auth=${localStorage.getItem("token")}`;
  const a = document.createElement("a");
  a.href = url;
  a.download = "";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function Presupuesto() {
  const { can } = useAuth();
  const canEdit = can("presupuesto", "edit");
  const now = new Date();
  const [view, setView] = useState("list");
  const [detailId, setDetailId] = useState(null);

  // Lista
  const [data, setData] = useState({ items: [], total: 0, page: 1 });
  const [fMes, setFMes] = useState("");
  const [fAnio, setFAnio] = useState("");
  const [page, setPage] = useState(1);

  // Generar
  const [genOpen, setGenOpen] = useState(false);
  const [genAnio, setGenAnio] = useState(String(now.getFullYear()));
  const [genMes, setGenMes] = useState(String(now.getMonth() + 1));
  const [generating, setGenerating] = useState(false);

  // Firmas de autorización
  const [firmasOpen, setFirmasOpen] = useState(false);
  const [firmas, setFirmas] = useState({ elaboro_nombre: "", elaboro_depto: "", autorizo_nombre: "", autorizo_depto: "", vobo_nombre: "", vobo_depto: "" });
  const [savingFirmas, setSavingFirmas] = useState(false);

  const openFirmas = async () => {
    try {
      const r = await api.get("/presupuesto/firmas");
      setFirmas(r.data);
    } catch { /* usa vacíos */ }
    setFirmasOpen(true);
  };
  const saveFirmas = async () => {
    setSavingFirmas(true);
    try {
      await api.put("/presupuesto/firmas", firmas);
      toast.success("Firmas guardadas");
      setFirmasOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingFirmas(false);
    }
  };

  const loadList = () => {
    const p = new URLSearchParams();
    if (fAnio) p.append("anio", fAnio);
    if (fMes) p.append("mes", fMes);
    p.append("page", page);
    api.get(`/presupuesto/list?${p.toString()}`).then((r) => setData(r.data));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (view === "list") loadList(); }, [fMes, fAnio, page, view]);

  const totalPages = Math.max(1, Math.ceil(data.total / 10));

  const generar = async () => {
    setGenerating(true);
    try {
      await api.post("/presupuesto/generar", { anio: parseInt(genAnio), mes: parseInt(genMes) });
      toast.success("Presupuesto generado");
      setGenOpen(false);
      setPage(1);
      loadList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const delPres = async (id) => {
    if (!window.confirm("¿Eliminar este presupuesto?")) return;
    await api.delete(`/presupuesto/${id}`);
    toast.success("Presupuesto eliminado");
    loadList();
  };

  const openDetail = (id) => { setDetailId(id); setView("detail"); };

  if (view === "detail") {
    return <PresupuestoDetalle pid={detailId} canEdit={canEdit} onBack={() => { setView("list"); loadList(); }} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="pres-filtro-mes" value={fMes} onChange={(e) => { setFMes(e.target.value); setPage(1); }} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900">
            <option value="">Todos los meses</option>
            {MESES.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
          </select>
          <input data-testid="pres-filtro-anio" type="number" placeholder="Año" value={fAnio} onChange={(e) => { setFAnio(e.target.value); setPage(1); }} className="w-28 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button data-testid="pres-firmas-btn" onClick={openFirmas} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50 flex items-center gap-2">
              <PenLine className="h-4 w-4" /> Configurar firmas
            </button>
            <button data-testid="pres-generar-btn" onClick={() => setGenOpen(true)} className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 flex items-center gap-2">
              <Plus className="h-4 w-4" /> Generar presupuesto
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 border-y border-slate-200">
            <tr>
              <th className="px-4 py-3 font-medium text-left">Nombre Presupuesto</th>
              <th className="px-4 py-3 font-medium text-right">Total</th>
              <th className="px-4 py-3 font-medium text-right w-32">Acciones</th>
            </tr>
          </thead>
          <tbody data-testid="presupuestos-table">
            {data.items.length === 0 && <tr><td colSpan={3} className="px-4 py-10 text-center text-slate-400">Sin presupuestos. Genera uno a partir de la estructura de la empresa.</td></tr>}
            {data.items.map((p) => (
              <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-3 font-medium text-slate-900">{p.nombre}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold">{fmtMoney(p.total, "MXN")}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`pres-ver-${p.id}`} onClick={() => openDetail(p.id)} className="text-slate-400 hover:text-slate-900 mr-3" title="Ver detalle"><Eye className="h-4 w-4" /></button>
                  <button data-testid={`pres-export-${p.id}`} onClick={() => exportXlsx(p.id)} className="text-emerald-600 hover:text-emerald-700 mr-3" title="Exportar Excel"><FileSpreadsheet className="h-4 w-4" /></button>
                  {canEdit && <button data-testid={`pres-del-${p.id}`} onClick={() => delPres(p.id)} className="text-slate-400 hover:text-red-600" title="Eliminar"><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
          <span>{data.total} presupuesto(s)</span>
          <div className="flex items-center gap-2">
            <button data-testid="pres-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
            <span>Página {page} de {totalPages}</span>
            <button data-testid="pres-next" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {genOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setGenOpen(false); }}>
          <div className="bg-white rounded-md shadow-lg w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="pres-gen-dialog">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">Generar presupuesto</h3>
              <button onClick={() => setGenOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-500">Se generará a partir de la estructura presupuestal activa de la empresa.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500">Año</label>
                  <input data-testid="gen-anio" type="number" value={genAnio} onChange={(e) => setGenAnio(e.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Mes</label>
                  <select data-testid="gen-mes" value={genMes} onChange={(e) => setGenMes(e.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900">
                    {MESES.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
                  </select>
                </div>
              </div>
              <button data-testid="gen-confirm" onClick={generar} disabled={generating} className="w-full bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 flex items-center justify-center gap-2 disabled:opacity-50">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Generar
              </button>
            </div>
          </div>
        </div>
      )}

      {firmasOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setFirmasOpen(false); }}>
          <div className="bg-white rounded-md shadow-lg w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="pres-firmas-dialog">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">Firmas de autorización</h3>
              <button onClick={() => setFirmasOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-500">Estas 3 firmas se imprimen al pie del Excel de todos los presupuestos de la empresa.</p>
              {[
                ["Elaboró", "elaboro"],
                ["Autorizó", "autorizo"],
                ["Vo. Bo.", "vobo"],
              ].map(([label, key]) => (
                <div key={key} className="border border-slate-200 rounded-md p-3">
                  <p className="text-xs font-semibold text-slate-700 mb-2">{label}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500">Nombre</label>
                      <input data-testid={`firma-${key}-nombre`} value={firmas[`${key}_nombre`] || ""} onChange={(e) => setFirmas((f) => ({ ...f, [`${key}_nombre`]: e.target.value }))} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Departamento</label>
                      <input data-testid={`firma-${key}-depto`} value={firmas[`${key}_depto`] || ""} onChange={(e) => setFirmas((f) => ({ ...f, [`${key}_depto`]: e.target.value }))} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
                    </div>
                  </div>
                </div>
              ))}
              <button data-testid="firmas-save" onClick={saveFirmas} disabled={savingFirmas} className="w-full bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 flex items-center justify-center gap-2 disabled:opacity-50">
                {savingFirmas ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Guardar firmas
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PresupuestoDetalle({ pid, canEdit, onBack }) {
  const [pres, setPres] = useState(null);
  const [detalle, setDetalle] = useState([]);
  const [collapsed, setCollapsed] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [addingPadre, setAddingPadre] = useState(null);
  const [addForm, setAddForm] = useState({ hijo: "", cantidad: "" });

  const load = () => api.get(`/presupuesto/${pid}`).then((r) => { setPres(r.data.presupuesto); setDetalle(r.data.detalle); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [pid]);

  const grupos = {};
  detalle.forEach((d) => { (grupos[d.padre] = grupos[d.padre] || []).push(d); });
  const total = detalle.reduce((s, d) => s + Number(d.subtotal || 0), 0);

  const toggle = (padre) => setCollapsed((s) => { const n = new Set(s); n.has(padre) ? n.delete(padre) : n.add(padre); return n; });

  const save = async (d) => {
    try {
      await api.put(`/presupuesto/detalle/${d.id}`, { cantidad: parseFloat(editVal) || 0 });
      setEditing(null);
      load();
      toast.success("Cantidad actualizada");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveCustom = async (padre) => {
    if (!addForm.hijo.trim()) { toast.error("Captura el nombre del concepto"); return; }
    try {
      await api.post(`/presupuesto/${pid}/detalle`, { padre, hijo: addForm.hijo, cantidad: parseFloat(addForm.cantidad) || 0 });
      toast.success("Concepto agregado");
      setAddingPadre(null);
      setAddForm({ hijo: "", cantidad: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delDetalle = async (did) => {
    if (!window.confirm("¿Eliminar este concepto?")) return;
    try {
      await api.delete(`/presupuesto/detalle/${did}`);
      toast.success("Concepto eliminado");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button data-testid="pres-back" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> Volver</button>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <h2 className="font-heading text-lg font-semibold text-slate-900">{pres?.nombre || "Presupuesto"}</h2>
            {canEdit && <p className="text-xs text-slate-500">Haz clic en una cantidad para editarla</p>}
          </div>
          <button data-testid="pres-export-detalle" onClick={() => exportXlsx(pid)} className="bg-emerald-600 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-emerald-700 flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" /> Exportar Excel
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 border-y border-slate-200">
            <tr>
              <th className="px-4 py-3 font-medium text-left">Concepto</th>
              <th className="px-4 py-3 font-medium text-right">Cantidad</th>
              {canEdit && <th className="px-4 py-3 w-10" />}
            </tr>
          </thead>
          <tbody data-testid="presupuesto-detalle">
            {Object.keys(grupos).length === 0 && <tr><td colSpan={canEdit ? 3 : 2} className="px-4 py-10 text-center text-slate-400">Sin partidas</td></tr>}
            {Object.entries(grupos).map(([padre, hijos]) => {
              const subtotal = hijos.reduce((s, h) => s + Number(h.subtotal || 0), 0);
              const isCol = collapsed.has(padre);
              return (
                <FragmentGroup key={padre} padre={padre} hijos={hijos} subtotal={subtotal} isCol={isCol} toggle={toggle}
                  canEdit={canEdit} editing={editing} setEditing={setEditing} editVal={editVal} setEditVal={setEditVal} save={save}
                  addingPadre={addingPadre} setAddingPadre={setAddingPadre} addForm={addForm} setAddForm={setAddForm} saveCustom={saveCustom} delDetalle={delDetalle} />
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 border-t border-slate-200">
              <td className="px-4 py-3 font-semibold text-slate-900">Total General</td>
              <td className="px-4 py-3 text-right font-bold tabular-nums text-slate-900" data-testid="pres-detalle-total">{fmtMoney(total, "MXN")}</td>
              {canEdit && <td />}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function FragmentGroup({ padre, hijos, subtotal, isCol, toggle, canEdit, editing, setEditing, editVal, setEditVal, save, addingPadre, setAddingPadre, addForm, setAddForm, saveCustom, delDetalle }) {
  return (
    <>
      <tr className="border-b border-slate-100 bg-slate-50/40">
        <td className="px-4 py-2.5">
          <button onClick={() => toggle(padre)} className="inline-flex items-center gap-1 font-medium text-slate-900" data-testid={`grupo-${padre}`}>
            {isCol ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />} {padre}
          </button>
        </td>
        <td className="px-4 py-2.5 text-right tabular-nums text-slate-500 italic">{fmtMoney(subtotal, "MXN")}</td>
        {canEdit && <td />}
      </tr>
      {!isCol && hijos.filter((h) => !h.placeholder && (h.hijo || "").trim()).map((h) => (
        <tr key={h.id} className="border-b border-slate-100 hover:bg-slate-50/50">
          <td className="px-4 py-2.5 text-slate-700" style={{ paddingLeft: 40 }}>
            {h.hijo}
            {h.personalizado && <span className="ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700" data-testid={`badge-personalizado-${h.id}`}>Personalizado</span>}
          </td>
          <td className="px-4 py-2.5 text-right tabular-nums">
            {editing === h.id ? (
              <div className="flex items-center justify-end gap-1">
                <input autoFocus type="number" step="any" value={editVal} onChange={(e) => setEditVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(h); if (e.key === "Escape") setEditing(null); }} className="w-32 rounded border border-slate-300 px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-slate-900" />
                <button onClick={() => save(h)} className="text-emerald-600" title="Guardar" data-testid={`detalle-save-${h.id}`}><Check className="h-4 w-4" /></button>
                <button onClick={() => setEditing(null)} className="text-slate-400" title="Cancelar"><X className="h-4 w-4" /></button>
              </div>
            ) : canEdit ? (
              <button onClick={() => { setEditing(h.id); setEditVal(String(h.cantidad)); }} className="inline-flex items-center justify-end gap-1.5 group" data-testid={`detalle-cantidad-${h.id}`}>
                <span className="tabular-nums group-hover:underline">{fmtMoney(h.cantidad, "MXN")}</span>
                <Pencil className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-900" />
              </button>
            ) : (
              <span className="tabular-nums" data-testid={`detalle-cantidad-${h.id}`}>{fmtMoney(h.cantidad, "MXN")}</span>
            )}
          </td>
          {canEdit && (
            <td className="px-2 py-2.5 text-right">
              {h.personalizado && <button onClick={() => delDetalle(h.id)} className="text-slate-400 hover:text-red-600" title="Eliminar concepto" data-testid={`detalle-del-${h.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
            </td>
          )}
        </tr>
      ))}
      {!isCol && canEdit && (
        addingPadre === padre ? (
          <tr className="border-b border-slate-100 bg-amber-50/40">
            <td className="px-4 py-2" style={{ paddingLeft: 40 }}>
              <input autoFocus placeholder="Nuevo concepto" value={addForm.hijo} onChange={(e) => setAddForm({ ...addForm, hijo: e.target.value })} className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" data-testid={`add-hijo-input-${padre}`} />
            </td>
            <td className="px-4 py-2 text-right">
              <div className="flex items-center justify-end gap-1">
                <input type="number" step="any" placeholder="0" value={addForm.cantidad} onChange={(e) => setAddForm({ ...addForm, cantidad: e.target.value })} className="w-28 rounded border border-slate-300 px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-slate-900" data-testid={`add-cant-input-${padre}`} />
                <button onClick={() => saveCustom(padre)} className="text-emerald-600" title="Guardar" data-testid={`add-save-${padre}`}><Check className="h-4 w-4" /></button>
                <button onClick={() => setAddingPadre(null)} className="text-slate-400" title="Cancelar"><X className="h-4 w-4" /></button>
              </div>
            </td>
            <td />
          </tr>
        ) : (
          <tr className="border-b border-slate-100">
            <td className="px-4 py-2" style={{ paddingLeft: 40 }} colSpan={3}>
              <button onClick={() => { setAddingPadre(padre); setAddForm({ hijo: "", cantidad: "" }); }} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid={`add-concepto-${padre}`}>
                <Plus className="h-3.5 w-3.5" /> Agregar concepto
              </button>
            </td>
          </tr>
        )
      )}
    </>
  );
}
